import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, TRUST } from "../../data";
import { Reveal, Hexagon } from "./primitives";

const Lightbox = ({ index, onClose, onNav }) => {
  const g = GALLERY[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [onClose, onNav]);

  return (
    <motion.div
      data-testid="lightbox"
      className="fixed inset-0 z-[80] bg-brand-black/95 backdrop-blur-md flex items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-6 right-6 z-10 w-12 h-12 grid place-items-center border border-brand-concrete text-white/70 hover:text-white hover:border-brand-red transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        className="absolute left-4 md:left-10 z-10 w-12 h-12 grid place-items-center border border-brand-concrete text-white/70 hover:text-white hover:border-brand-red transition-colors"
        aria-label="Previous photo"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        className="absolute right-4 md:right-10 z-10 w-12 h-12 grid place-items-center border border-brand-concrete text-white/70 hover:text-white hover:border-brand-red transition-colors"
        aria-label="Next photo"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <motion.figure
        key={index}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={g.img}
          alt={g.label}
          data-testid="lightbox-image"
          className="max-w-[88vw] max-h-[75vh] object-contain border border-brand-concrete"
        />
        <figcaption className="mt-4 flex items-center justify-between gap-4">
          <span className="flex items-center gap-3">
            <span className="w-6 h-[2px] bg-brand-red" />
            <span className="font-anton uppercase text-xl md:text-2xl tracking-wide">{g.label}</span>
          </span>
          <span data-testid="lightbox-counter" className="font-mono-p text-xs text-white/50 tracking-[0.2em]">
            {String(index + 1).padStart(2, "0")} / {String(GALLERY.length).padStart(2, "0")}
          </span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

export const Gallery = () => {
  const [active, setActive] = useState(null);

  const nav = useCallback(
    (dir) => setActive((i) => (i + dir + GALLERY.length) % GALLERY.length),
    []
  );
  const close = useCallback(() => setActive(null), []);

  return (
    <section id="work" data-testid="gallery" className="relative py-24 md:py-36 px-6 md:px-12 lg:px-16 bg-brand-black">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono-p text-xs uppercase tracking-[0.28em] text-brand-red">In The Field</span>
            <h2 className="mt-4 font-anton uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">
              The Work<br />Speaks.
            </h2>
          </div>
          <p className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/40">
            Click a photo to enlarge
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {GALLERY.map((g, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}>
            <button
              data-testid={`gallery-item-${i}`}
              data-cursor="View"
              onClick={() => setActive(i)}
              className={`group relative block w-full text-left overflow-hidden border border-brand-concrete ${i % 2 === 0 ? "clip-corner" : "clip-corner-tl"} ${i % 3 === 1 ? "h-72 md:h-96" : "h-60 md:h-72"}`}
            >
              <img src={g.img} alt={g.label} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-brand-black/40 group-hover:bg-brand-black/10 transition-colors duration-500" />
              <span className="absolute bottom-4 left-4 font-anton uppercase text-xl tracking-wide text-white drop-shadow-lg">
                {g.label}
              </span>
              <span className="absolute top-4 right-4 font-mono-p text-[10px] tracking-[0.2em] text-white/0 group-hover:text-white/70 transition-colors duration-400">
                [ VIEW ]
              </span>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active !== null && <Lightbox index={active} onClose={close} onNav={nav} />}
      </AnimatePresence>

      {/* Trust badges */}
      <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-concrete border border-brand-concrete">
        {TRUST.map((t, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div data-testid={`trust-${i}`} className="bg-brand-black h-full p-8 group hover:bg-brand-asphalt transition-colors duration-400">
              <Hexagon className="w-12 h-12 bg-brand-red mb-5 group-hover:scale-110 transition-transform duration-400">
                <span className="font-anton text-white text-sm">0{i + 1}</span>
              </Hexagon>
              <h3 className="font-anton uppercase text-xl tracking-tight leading-tight">{t.title}</h3>
              <p className="mt-2 font-ibm text-sm text-white/50">{t.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
